/**
 * NotificationBell Component
 * Bell icon with dropdown showing notifications (including @mentions)
 */

import { useState, useRef, useEffect } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, CheckCheck, Trash2, User, X, AtSign, BellRing, BellOff, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  subscribeToNotifications
} from '../services/notifications'
import {
  isPushSupported,
  isPushSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush
} from '../services/pushNotifications'
import { useAuth } from '../lib/auth'
import type { Notification } from '../types/notification'

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [showPushSettings, setShowPushSettings] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { profile } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Check if push is supported
  const pushSupported = isPushSupported()

  // Check if push is subscribed
  const { data: pushEnabled = false, isLoading: isPushLoading } = useQuery({
    queryKey: ['push-subscribed'],
    queryFn: isPushSubscribed,
    enabled: pushSupported && isOpen,
    staleTime: 60000
  })

  // Toggle push subscription
  const togglePushMutation = useMutation({
    mutationFn: async () => {
      if (pushEnabled) {
        return unsubscribeFromPush()
      } else {
        return subscribeToPush()
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['push-subscribed'] })
      } else if (result.error) {
        alert(result.error)
      }
    }
  })

  // Test push notification
  const testPushMutation = useMutation({
    mutationFn: sendTestPush,
    onSuccess: (result) => {
      if (!result.success && result.error) {
        alert(result.error)
      }
    }
  })

  // Fetch unread count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 30000 // Refetch every 30 seconds
  })

  // Fetch notifications when dropdown is open
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', showAll],
    queryFn: () => getNotifications(20, showAll),
    enabled: isOpen
  })

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!profile?.user_id) return

    const unsubscribe = subscribeToNotifications(profile.user_id, () => {
      // Refetch when new notification arrives
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    })

    return () => {
      unsubscribe()
    }
  }, [profile?.user_id, queryClient])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      await markAsRead(notification.id)
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    }

    // Navigate if link exists
    if (notification.link) {
      setIsOpen(false)
      let link = notification.link

      // Transform old ?edit= links to new direct links
      const editMatch = link.match(/\/admin\/assignments\?edit=(.+)/)
      if (editMatch) {
        link = `/admin/assignments/${editMatch[1]}`
      }

      // For subcontractors, transform admin links to subcontractor links
      if (profile?.role === 'subcontractor') {
        // Transform /admin/assignments/xxx to /subcontractor/assignments/xxx
        if (link.startsWith('/admin/assignments/')) {
          link = link.replace('/admin/assignments/', '/subcontractor/assignments/')
        }
        // Transform /admin/customers/xxx - TG shouldn't access customer pages
        // Transform /admin/subcontractors/xxx - TG shouldn't access other TG pages
        // For non-assignment links, just go to dashboard
        if (link.startsWith('/admin/')) {
          link = '/subcontractor'
        }
      }

      navigate(link)
    }
  }

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
    queryClient.invalidateQueries({ queryKey: ['notifications'] })
    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
  }

  const handleDelete = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation()
    await deleteNotification(notificationId)
    queryClient.invalidateQueries({ queryKey: ['notifications'] })
    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'mention':
        return <AtSign className="h-4 w-4 text-primary-500" />
      case 'comment':
        return <User className="h-4 w-4 text-blue-500" />
      default:
        return <Bell className="h-4 w-4 text-nature-500" />
    }
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just nu'
    if (diffMins < 60) return `${diffMins} min sedan`
    if (diffHours < 24) return `${diffHours} tim sedan`
    if (diffDays < 7) return `${diffDays} dagar sedan`

    return format(date, 'd MMM', { locale: sv })
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-nature-600 dark:text-nature-400 hover:text-nature-900 dark:hover:text-nature-100 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
        title="Notifikationer"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="fixed left-1/2 -translate-x-1/2 top-16 w-[calc(100vw-2rem)] max-w-80 sm:max-w-96 md:absolute md:left-auto md:translate-x-0 md:right-0 md:top-auto md:mt-2 md:w-96 bg-white dark:bg-nature-800 rounded-lg shadow-xl border border-nature-200 dark:border-nature-700 z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-nature-200 dark:border-nature-700">
            <h3 className="font-semibold text-nature-900 dark:text-nature-100">
              Notifikationer
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  title="Markera alla som lästa"
                >
                  <CheckCheck className="h-4 w-4" />
                  <span className="hidden sm:inline">Markera alla</span>
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-nature-500 hover:text-nature-700 dark:hover:text-nature-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex border-b border-nature-200 dark:border-nature-700">
            <button
              onClick={() => { setShowAll(false); setShowPushSettings(false) }}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                !showAll && !showPushSettings
                  ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500'
                  : 'text-nature-600 dark:text-nature-400 hover:text-nature-900 dark:hover:text-nature-100'
              }`}
            >
              Olästa ({unreadCount})
            </button>
            <button
              onClick={() => { setShowAll(true); setShowPushSettings(false) }}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                showAll && !showPushSettings
                  ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500'
                  : 'text-nature-600 dark:text-nature-400 hover:text-nature-900 dark:hover:text-nature-100'
              }`}
            >
              Alla
            </button>
            {pushSupported && (
              <button
                onClick={() => setShowPushSettings(!showPushSettings)}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  showPushSettings
                    ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500'
                    : 'text-nature-600 dark:text-nature-400 hover:text-nature-900 dark:hover:text-nature-100'
                }`}
                title="Push-inställningar"
              >
                {pushEnabled ? (
                  <BellRing className="h-4 w-4" />
                ) : (
                  <BellOff className="h-4 w-4" />
                )}
              </button>
            )}
          </div>

          {/* Push settings panel */}
          {showPushSettings && pushSupported && (
            <div className="px-4 py-3 border-b border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {pushEnabled ? (
                    <BellRing className="h-4 w-4 text-primary-500" />
                  ) : (
                    <BellOff className="h-4 w-4 text-nature-400" />
                  )}
                  <span className="text-sm font-medium text-nature-900 dark:text-nature-100">
                    Push-notiser
                  </span>
                </div>
                <button
                  onClick={() => togglePushMutation.mutate()}
                  disabled={togglePushMutation.isPending || isPushLoading}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                    ${pushEnabled
                      ? 'bg-primary-500'
                      : 'bg-nature-300 dark:bg-nature-600'
                    }
                    ${(togglePushMutation.isPending || isPushLoading) ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${pushEnabled ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                  {(togglePushMutation.isPending || isPushLoading) && (
                    <Loader2 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-white" />
                  )}
                </button>
              </div>
              <p className="text-xs text-nature-500 dark:text-nature-400">
                {pushEnabled
                  ? 'Du får push-notiser när du nämns i kommentarer'
                  : 'Aktivera för att få push-notiser på denna enhet'
                }
              </p>
              {pushEnabled && (
                <button
                  onClick={() => testPushMutation.mutate()}
                  disabled={testPushMutation.isPending}
                  className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
                >
                  {testPushMutation.isPending ? 'Skickar...' : 'Skicka testnotis'}
                </button>
              )}
            </div>
          )}

          {/* Notifications list */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-nature-500">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {showAll ? 'Inga notifikationer' : 'Inga olästa notifikationer'}
                </p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`
                    flex gap-3 px-4 py-3 cursor-pointer transition-colors
                    ${notification.is_read
                      ? 'bg-white dark:bg-nature-800'
                      : 'bg-primary-50 dark:bg-primary-900/20'
                    }
                    hover:bg-nature-50 dark:hover:bg-nature-700
                    border-b border-nature-100 dark:border-nature-700 last:border-b-0
                  `}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${
                      notification.is_read
                        ? 'text-nature-700 dark:text-nature-300'
                        : 'text-nature-900 dark:text-nature-100 font-medium'
                    }`}>
                      {notification.title}
                    </p>
                    {notification.message && (
                      <p className="text-xs text-nature-500 mt-0.5 truncate">
                        {notification.message}
                      </p>
                    )}
                    <p className="text-xs text-nature-400 mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 flex items-start gap-1">
                    {!notification.is_read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          markAsRead(notification.id).then(() => {
                            queryClient.invalidateQueries({ queryKey: ['notifications'] })
                            queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
                          })
                        }}
                        className="p-1 text-nature-400 hover:text-primary-500 transition-colors"
                        title="Markera som läst"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDelete(e, notification.id)}
                      className="p-1 text-nature-400 hover:text-red-500 transition-colors"
                      title="Ta bort"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
